import React from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Box, Text, HStack, VStack, Icon
} from "@chakra-ui/react";
import {FaRegCopy, FaRegEdit, FaRegTrashAlt, FaCog} from "react-icons/fa";
import {getHourDecimals} from "./hourPrecision";
import {HolidayNotice} from "./WeekActionsUI";

interface HelpDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const TOPICS = [
    {icon: FaRegCopy, title: "Copy Week", body: "Copies all hour entries from a past week into the current week. Existing target-day bookings are locked; empty days on the same row are pre-filled and can be edited."},
    {icon: FaRegEdit, title: "Edit", body: "Edit existing bookings for the current week. Approved cells are locked."},
    {icon: FaRegTrashAlt, title: "Clear", body: "Sets every not-yet-approved booking for the current week to 0 hours after confirmation. Bookings already approved/sent are left untouched."},
    {icon: FaCog, title: "Settings", body: "Manage your favorite project codes (JIRA ID, summary, comment, category) and the hour step (1, 0.5, 0.25, 0.1, 0.01 or 0.001, default 0.01) on the Preferences page (gear icon). Favorites appear in a dropdown at the top of Copy/Edit so you can quickly add a new row. Both are saved to your account and apply the next time you open Copy or Edit."}
];

/** Static explanation of the toolbar actions. Ported from script's showHelpDialog. */
export function HelpDialog({isOpen, onClose}: HelpDialogProps) {
    const decimals = getHourDecimals();

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>TimeBox Week Actions &mdash; How it works</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Text fontSize="sm" color="text-muted" mb={4}>
                        Buttons in the toolbar (Weekly view): <b>Copy Week</b>, <b>Edit</b>, <b>Clear</b>, <b>Help</b>,
                        and <b>Settings</b>. Hours are entered with up to {decimals} decimal{decimals === 1 ? "" : "s"}.
                    </Text>

                    <VStack align="stretch" spacing={3}>
                        {TOPICS.map((topic) => (
                            <HStack key={topic.title} align="start" borderWidth="1px" borderColor="border-subtle" borderRadius="md" p={3} spacing={3}>
                                <Icon as={topic.icon} boxSize={4} mt={1} color="text-subtle"/>
                                <Box>
                                    <Text fontWeight={600}>{topic.title}</Text>
                                    <Text fontSize="sm" color="gray.700" _dark={{color: "gray.300"}}>{topic.body}</Text>
                                </Box>
                            </HStack>
                        ))}
                        <HolidayNotice/>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="blue" onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
